import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Star, X, SlidersHorizontal, MapPin, Search } from "lucide-react";
import { translateBoardType } from "@/utils/boardTypeTranslations";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/contexts/LocaleContext";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";

export interface FilterState {
  priceRange: [number, number];
  sortBy: string;
  boardTypes: string[];
  starRatings: number[];
  cancellationPolicy: string;
  hotelNameSearch: string;
  recommendedOnly: boolean;
}

interface HotelFiltersSidebarProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  maxPrice: number;
  boardTypes: string[];
  starRatings: number[];
  resultsCount: number;
  destinationCoordinates?: { lat: number; lng: number } | null;
  onOpenMap?: () => void;
  hasRecommendedHotels?: boolean;
}

export const HotelFiltersSidebar = ({
  filters,
  onFiltersChange,
  maxPrice,
  boardTypes,
  starRatings,
  resultsCount,
  destinationCoordinates,
  onOpenMap,
  hasRecommendedHotels,
}: HotelFiltersSidebarProps) => {
  const { t, i18n } = useTranslation();
  const { formatPrice } = useLocale();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);
  const [isLoadingMap, setIsLoadingMap] = useState(true);

  // Keep latest filters in a ref to avoid recreating handlers and to prevent typing jank
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);
  
  // Local state for smooth slider interaction
  const [localPriceRange, setLocalPriceRange] = useState<[number, number]>(filters.priceRange);

  // Local state for hotel name search with debounce
  const [localSearchName, setLocalSearchName] = useState(filters.hotelNameSearch || '');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local price range when filters change externally
  useEffect(() => {
    setLocalPriceRange(filters.priceRange);
  }, [filters.priceRange]);

  // Sync local search name when filters change externally
  useEffect(() => {
    setLocalSearchName(filters.hotelNameSearch || '');
  }, [filters.hotelNameSearch]);

  useEffect(() => {
    const fetchMapboxToken = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-mapbox-token');
        if (error) throw error;
        setMapboxToken(data.token);
      } catch (error) {
        console.error('Error fetching mapbox token:', error);
      } finally {
        setIsLoadingMap(false);
      }
    };
    fetchMapboxToken();
  }, []);

  // Handle price slider - update local state immediately, debounce filter update
  const handlePriceChange = (value: number[]) => {
    setLocalPriceRange([value[0], value[1]]);
  };

  const handlePriceChangeCommit = (value: number[]) => {
    const current = filtersRef.current;
    onFiltersChange({ ...current, priceRange: [value[0], value[1]] });
  };

  const handleStarRatingToggle = (rating: number) => {
    const newRatings = filters.starRatings.includes(rating)
      ? filters.starRatings.filter(r => r !== rating)
      : [...filters.starRatings, rating];
    onFiltersChange({ ...filters, starRatings: newRatings });
  };

  const handleBoardTypeToggle = (board: string) => {
    const newBoards = filters.boardTypes.includes(board)
      ? filters.boardTypes.filter(b => b !== board)
      : [...filters.boardTypes, board];
    onFiltersChange({ ...filters, boardTypes: newBoards });
  };

  const handleSearchNameChange = (value: string) => {
    setLocalSearchName(value);
    
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    
    searchDebounceRef.current = setTimeout(() => {
      const current = filtersRef.current;
      onFiltersChange({ ...current, hotelNameSearch: value });
    }, 500);
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const handleClearFilters = () => {
    onFiltersChange({
      priceRange: [0, maxPrice],
      sortBy: "default",
      boardTypes: [],
      starRatings: [],
      cancellationPolicy: "all",
      hotelNameSearch: "",
      recommendedOnly: false,
    });
  };

  const getStaticMapUrl = () => {
    if (!mapboxToken || !destinationCoordinates) return null;
    const { lat, lng } = destinationCoordinates;
    return `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/${lng},${lat},11,0/280x160@2x?access_token=${mapboxToken}`;
  };

  const activeFiltersCount = 
    (filters.boardTypes.length > 0 ? 1 : 0) +
    (filters.starRatings.length > 0 ? 1 : 0) +
    (filters.cancellationPolicy !== 'all' ? 1 : 0) +
    (filters.priceRange[0] > 0 || filters.priceRange[1] < maxPrice ? 1 : 0) +
    (filters.hotelNameSearch ? 1 : 0) +
    (filters.recommendedOnly ? 1 : 0);

  const renderMapSnapshot = () => (
    <div className="space-y-4">
      {/* Map Preview - Hidden on mobile */}
      <div
        className="relative w-full h-40 rounded-lg overflow-hidden cursor-pointer group border border-border"
        onClick={onOpenMap}
      >
        {isLoadingMap ? (
          <Skeleton className="w-full h-full" />
        ) : getStaticMapUrl() ? (
          <>
            <img
              src={getStaticMapUrl()!}
              alt={t("filters.mapPreview")}
              className="w-full h-full object-cover transition-transform group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors" />
            <Button
              variant="secondary"
              size="sm"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 shadow-lg"
            >
              <MapPin className="mr-2 h-4 w-4" />
              {t("filters.showOnMap")}
            </Button>
          </>
        ) : (
          <div className="w-full h-full bg-muted flex items-center justify-center">
            <Button variant="secondary" size="sm" onClick={onOpenMap}>
              <MapPin className="mr-2 h-4 w-4" />
              {t("filters.showOnMap")}
            </Button>
          </div>
        )}
      </div>

      {/* Hotel Name Search */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-foreground">
          {t("filters.searchByName")}
        </Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder={t("filters.searchPlaceholder")}
            value={localSearchName}
            onChange={(e) => handleSearchNameChange(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>
    </div>
  );

  // Render search input for mobile (without map)
  const renderMobileSearchInput = () => (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder={t("filters.searchPlaceholder")}
          value={localSearchName}
          onChange={(e) => handleSearchNameChange(e.target.value)}
          className="pl-10"
        />
      </div>
    </div>
  );

  const renderFiltersContent = () => (
    <div className="space-y-6">
      {/* Recommended Hotels Filter - Only show when there are recommended hotels */}
      {hasRecommendedHotels && (
        <div className="space-y-3">
          <Label className="text-sm font-semibold text-foreground">
            {t("filters.recommendations")}
          </Label>
          <div className="flex items-center space-x-3">
            <Checkbox
              id="recommended-only"
              checked={filters.recommendedOnly}
              onCheckedChange={(checked) =>
                onFiltersChange({ ...filters, recommendedOnly: !!checked })
              }
            />
            <label
              htmlFor="recommended-only"
              className="flex items-center gap-2 text-sm cursor-pointer"
            >
              <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
              {t("filters.recommendedOnly")}
            </label>
          </div>
        </div>
      )}

      {/* Price Range */}
      <div className="space-y-4">
        <Label className="text-sm font-semibold text-foreground">
          {t("filters.pricePerNight")}
        </Label>
        <Slider
          min={0}
          max={maxPrice}
          step={0.01}
          value={localPriceRange}
          onValueChange={handlePriceChange}
          onValueCommit={handlePriceChangeCommit}
          className="w-full"
        />
        <div className="flex justify-between text-sm text-muted-foreground">
          <span>{formatPrice(localPriceRange[0])}</span>
          <span>{formatPrice(localPriceRange[1])}</span>
        </div>
      </div>

      {/* Cancellation Policy */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold text-foreground">
          {t("filters.cancellationPolicy")}
        </Label>
        <Select
          value={filters.cancellationPolicy}
          onValueChange={(value) =>
            onFiltersChange({ ...filters, cancellationPolicy: value })
          }
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t("filters.all")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("filters.all")}</SelectItem>
            <SelectItem value="refundable">{t("filters.refundable")}</SelectItem>
            <SelectItem value="non-refundable">
              {t("filters.nonRefundable")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Board Type */}
      {boardTypes.length > 0 && (
        <div className="space-y-3">
          <Label className="text-sm font-semibold text-foreground">
            {t("filters.boardType")}
          </Label>
          <div className="space-y-2">
            {boardTypes.map((board) => (
              <div key={board} className="flex items-center space-x-3">
                <Checkbox
                  id={`board-${board}`}
                  checked={filters.boardTypes.includes(board)}
                  onCheckedChange={() => handleBoardTypeToggle(board)}
                />
                <label
                  htmlFor={`board-${board}`}
                  className="text-sm cursor-pointer"
                >
                  {translateBoardType(board, i18n.language)}
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Star Rating */}
      {starRatings.length > 0 && (
        <div className="space-y-3">
          <Label className="text-sm font-semibold text-foreground">
            {t("filters.stars")}
          </Label>
          <div className="space-y-2">
            {starRatings.map((rating) => (
              <div key={rating} className="flex items-center space-x-3">
                <Checkbox
                  id={`star-${rating}`}
                  checked={filters.starRatings.includes(rating)}
                  onCheckedChange={() => handleStarRatingToggle(rating)}
                />
                <label
                  htmlFor={`star-${rating}`}
                  className="flex items-center gap-1 text-sm cursor-pointer"
                >
                  {Array.from({ length: Math.floor(rating) }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                  ))}
                  {rating % 1 !== 0 && (
                    <div className="relative h-4 w-4">
                      <Star className="absolute h-4 w-4 text-primary" />
                      <div className="absolute overflow-hidden" style={{ width: '50%' }}>
                        <Star className="h-4 w-4 fill-primary text-primary" />
                      </div>
                    </div>
                  )}
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Clear Filters */}
      {activeFiltersCount > 0 && (
        <Button variant="outline" onClick={handleClearFilters} className="w-full">
          <X className="mr-2 h-4 w-4" />
          {t("filters.clear")}
        </Button>
      )}
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block w-72 shrink-0">
        <div className="sticky top-24 space-y-6">
          {renderMapSnapshot()}
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-foreground">{t('filters.title')}</h3>
            {activeFiltersCount > 0 && (
              <span className="text-xs text-muted-foreground">
                {activeFiltersCount} {t('filters.active', 'ativos')}
              </span>
            )}
          </div>
          {renderFiltersContent()}
        </div>
      </aside>

      {/* Mobile Filter Button & Sheet - No map snapshot, just filter button */}
      <div className="lg:hidden">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <SlidersHorizontal className="h-4 w-4" />
              {t('filters.title')}
              {activeFiltersCount > 0 && (
                <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                  {activeFiltersCount}
                </span>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[300px] sm:w-[340px] overflow-y-auto">
            <SheetHeader>
              <SheetTitle>{t('filters.title')}</SheetTitle>
            </SheetHeader>
            <div className="mt-6 space-y-6">
              {renderMobileSearchInput()}
              {renderFiltersContent()}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
};